/**
 * feat-107 shared primitive (2/4) — IconPickerPopover (ADR 0022).
 *
 * Replaces the cluttered inline 12-tile icon grid (the biggest declutter win in
 * Slice A) with ONE round-swatch trigger that opens a floating 4×3 grid plus a
 * "Use custom emoji…" affordance. Reused in THREE places:
 *   - Lesson icon  (backed by UNIT_MASCOT_CATALOG → writes `mascot_key`/`emoji`)
 *   - Drill icon   (backed by LESSON_ICON_CATALOG → writes `icon_key`/`emoji`)
 *   - the importer's inferred-Drill cards (Slice B)
 *
 * PROPS CONTRACT IS FINAL (this ADR). The body here is a minimal, working
 * implementation; Slice A/B may polish styling/positioning/portal/focus-trap,
 * but MUST NOT change the props or the IconSelection shape.
 *
 * The service catalogs (`LESSON_ICON_CATALOG`/`UNIT_MASCOT_CATALOG`) store only
 * `{ key, label }` — NOT a display glyph. The web admin needs an emoji to render
 * each catalog key, so the caller supplies `glyphForKey` (the key→glyph map is
 * slice-owned; the catalog keys themselves stay the cross-app contract). A
 * selection is therefore a discriminated union: a catalog key, or a custom emoji.
 */

import React, { useRef, useState } from 'react';
import type { CatalogEntry } from '../../services/quizContentAdminService';

/** What the picker emits. The consuming form maps this onto the API fields:
 *  Drill → `icon_key` (catalog) / `emoji` (custom); Lesson → `mascot_key`
 *  (catalog) / `emoji` (custom). */
export type IconSelection =
  | { kind: 'catalog'; key: string }
  | { kind: 'custom'; emoji: string };

export interface IconPickerPopoverProps {
  /** Which catalog backs the 4×3 grid: LESSON_ICON_CATALOG or UNIT_MASCOT_CATALOG. */
  catalog: ReadonlyArray<CatalogEntry>;
  /** Current selection, or null when nothing is picked yet. */
  selection: IconSelection | null;
  /** Fired when the user taps a swatch or commits a custom emoji. */
  onSelect: (selection: IconSelection) => void;
  /**
   * Maps a catalog key → emoji glyph for display. Slice-owned, because the
   * service catalog stores key+label only (no glyph). Falls back to the first
   * letter of the catalog label when a key is unmapped.
   */
  glyphForKey: (key: string) => string;
  /** Accessible label for the round trigger button. */
  ariaLabel?: string;
  disabled?: boolean;
  className?: string;
}

/** Glyph to render on the trigger for the current selection. */
function triggerGlyph(
  selection: IconSelection | null,
  glyphForKey: (key: string) => string,
): string {
  if (!selection) return '＋';
  return selection.kind === 'custom' ? selection.emoji : glyphForKey(selection.key);
}

export function IconPickerPopover({
  catalog,
  selection,
  onSelect,
  glyphForKey,
  ariaLabel = 'Change icon',
  disabled = false,
  className = '',
}: IconPickerPopoverProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  const pickKey = (key: string) => {
    onSelect({ kind: 'catalog', key });
    setOpen(false);
  };
  const commitCustom = () => {
    const emoji = custom.trim();
    if (!emoji) return;
    onSelect({ kind: 'custom', emoji });
    setCustom('');
    setOpen(false);
  };

  return (
    <div ref={rootRef} className={`relative inline-block ${className}`}>
      <button
        type="button"
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="w-9 h-9 rounded-xl bg-gray-100 grid place-items-center text-lg hover:ring-2 hover:ring-coral/40 shrink-0 disabled:opacity-50"
      >
        {triggerGlyph(selection, glyphForKey)}
      </button>

      {open && (
        <div className="absolute left-0 top-[44px] z-30 w-64 bg-white border border-softBorder rounded-2xl shadow-xl p-3">
          <div className="text-xs font-bold text-inkMuted mb-2">Pick icon</div>
          <div className="grid grid-cols-4 gap-2">
            {catalog.map((entry) => {
              const isActive =
                selection?.kind === 'catalog' && selection.key === entry.key;
              return (
                <button
                  key={entry.key}
                  type="button"
                  title={entry.label}
                  onClick={() => pickKey(entry.key)}
                  className={`aspect-square rounded-lg grid place-items-center text-base ${
                    isActive
                      ? 'bg-coral-light ring-2 ring-coral'
                      : 'bg-gray-100 hover:bg-coral-light'
                  }`}
                >
                  {glyphForKey(entry.key)}
                </button>
              );
            })}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <input
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitCustom();
              }}
              placeholder="Use custom emoji…"
              className="flex-1 text-xs border border-softBorder rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-coral/40"
            />
            <button
              type="button"
              onClick={commitCustom}
              className="text-xs text-coral font-semibold px-2 py-1.5 rounded-lg hover:bg-coral-light"
            >
              Use
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default IconPickerPopover;
